import moment, { Moment } from 'moment';
import { HolidayRecurringDay } from '@features/holidays/models/Holiday.model';
import { agent } from './agent';
import { getFirstMondayInMonth } from '@shared/utils/DateUtils';
import { WhereaboutsOption } from "./WhereaboutOptions";
import { TeamEntity } from '@shared/models/team.models';
import { TagWithCount } from '@shared/models/tags.models';
import { AccountSummary } from '@features/auth/services/AccountService';
import { OfficeEntity } from "./AdvanceHotDeskingService";
import { MeetingRoom } from '@features/meeting-rooms/pages/models/MeetingRooms';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';

export class ConfigEntity {
  constructor(
    public holidayAllowance: boolean,
    public numberOfDesksAvailable: number,
    public companyHolidayAllowance: number,
    public holidayYearRecurringMonth: number,
    public holidayYearRecurringDay: HolidayRecurringDay,
    public carParkingEnabled?: boolean,
    public repeatWeeklySchedule?: boolean,
    public repeatScheduleWeeksLimit?: number,
    public restrictBookingWindowForTeamAdmins?: boolean,
    public visitorBookingEnabled?: boolean,
    public holidayYearEnd?: string,
    public holidayYearStart?: string,
    public autoCarryOverEnabled?: boolean,
    public autoCarryOverDaysLimit?: number,
    public autoCarryOverExpiryMonth?: number,
    public autoCarryOverExpiryDay?: number,
    public hidePastWhereaboutsEnabled?: boolean,
    public hidePastWhereaboutsWeeksLimit?: number,
    public checkinConfirmBookingsEnabled?: boolean,
    public checkinConfirmedVisibleToEveryone?: boolean,
    public checkinUpdateWhereaboutsEnabled?: boolean,) {}

    public getHolidayYearEndDate(date?: Moment): Moment | null {
      let endDate = date ?? moment();
      const holidayStartDate = this.getHolidayYearStartDate(endDate);
      endDate = holidayStartDate.add(1, 'years');
      endDate = endDate.subtract(1, 'days')
      return endDate
    }

    public getHolidayYearStartDate(date?: Moment): Moment {
      let startDate = (date ?? moment()).clone();
      const holidayMonthWithOffset = this.holidayYearRecurringMonth > 0 ? this.holidayYearRecurringMonth - 1 : this.holidayYearRecurringMonth;

      const holidayYear = startDate.month() < holidayMonthWithOffset ? startDate.year() - 1 : startDate.year();

      startDate = startDate.year(holidayYear);
      startDate =  startDate.month(holidayMonthWithOffset) // Zero based by moment is not
      startDate = this.holidayYearRecurringDay === HolidayRecurringDay.firstDayOfMonth ? startDate.date(1) : getFirstMondayInMonth(startDate);

      return startDate;
    }
}

export const DefaultConfigEntity: ConfigEntity = new ConfigEntity(false,
  0,
  1,
  0,
  HolidayRecurringDay.firstDayOfMonth)

function toConfigEntity(response: any): ConfigEntity {
  return new ConfigEntity(
    response.holidayAllowance,
    response.numberOfDesksAvailable,
    response.companyHolidayAllowance,
    response.holidayYearRecurringMonth,
    response.holidayYearRecurringDay,
    response.carParkingEnabled,
    response.repeatWeeklySchedule,
    response.repeatScheduleWeeksLimit,
    response.restrictBookingWindowForTeamAdmins,
    response.visitorBookingEnabled,
    response.holidayYearEndDate,
    response.holidayYearStartDate,
    response.autoCarryOverEnabled,
    response.autoCarryOverDaysLimit,
    response.autoCarryOverExpiryMonth,
    response.autoCarryOverExpiryDayOfMonth,
    response.hidePastWhereaboutsEnabled,
    response.hidePastWhereaboutsWeeksLimit,
    response.checkinConfirmBookingsEnabled,
    response.checkinConfirmedVisibleToEveryone,
    response.checkinUpdateWhereaboutsEnabled,
  )
}

export async function getConfig(): Promise<ConfigEntity> {
  const response = await agent.requests.get('/api/config');
  return toConfigEntity(response);
}

export async function postUpdateConfig(configEntity: ConfigEntity): Promise<ConfigEntity> {
  const response = await agent.requests.post(`/api/config`, configEntity);
  return toConfigEntity(response);
}

export async function setAdvanceHotDesking(value: boolean): Promise<ConfigEntity> {
  const response = await agent.requests.post(`/api/config/advance-hot-desking`, {advanceHotDesking: value});
  return toConfigEntity(response);
}

// Top-level keys of the /api/v2/config response. Pass some to fetch only those after an edit.
export type AppConfigSection = 'config' | 'whereaboutsOptions' | 'teams' | 'workGroups' | 'departments'
  | 'accountInfo' | 'offices' | 'meetingRooms';

export async function getAppConfig(sections?: AppConfigSection[]): Promise<Partial<AppConfig>> {
  const query = sections?.length ? `?sections=${sections.join(',')}` : '';
  return await agent.requests.get(`/api/v2/config${query}`);
}

export async function updateConfigProperty(property: 'carryOverEnabled' | 'carryOverDaysLimit' | 'carryOverExpiryMonth' | 'carryOverExpiryDayOfMonth', value: any): Promise<any> {
  const response = await agent.requests.put('/api/v2/config/update', {property, value});
  return response;
}

export interface AppConfig {
  config: ConfigEntity;
  whereaboutsOptions: WhereaboutsOption[];
  userConfig: any;
  teams: TeamEntity[];
  workGroups: TagWithCount[];
  // Only the limits and counts; the key is still accountInfo so already-open tabs keep working.
  accountInfo: AccountSummary;
  offices: OfficeEntity[];
  meetingRooms: MeetingRoom[];
  departments?: Department[];
}
